import React, { useEffect, useState } from 'react';
import ProfileFormSheet from '../modals/ProfileFormSheet';
import {
  AUDIENCE_EVERYONE,
  formatAudienceLabel,
  getEnabledPropertyKeys,
  getPropertyLabel,
  mergePropertyDefinitions
} from '../../constants/ugpProperties';

const UgpPropertyOpenTabIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const UgpPropertyEditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const UgpPropertiesSection = ({
  properties = [],
  assignmentMetadata = {},
  loading = false,
  canAdd = false,
  canEdit = false,
  canRemove = false,
  ownerUgpId = null,
  ownerGroupsPeriodsId = null,
  addSheetOpen = false,
  onAddSheetOpenChange,
  onReload
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [propertyKey, setPropertyKey] = useState('');
  const [audienceKind, setAudienceKind] = useState('everyone');
  const [audienceGpId, setAudienceGpId] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [audienceOptions, setAudienceOptions] = useState({ groups_periods: [] });
  const [loadingAudience, setLoadingAudience] = useState(false);

  const defs = mergePropertyDefinitions(assignmentMetadata);
  const enabledKeys = getEnabledPropertyKeys(assignmentMetadata);
  const orgGroupsPeriods = audienceOptions.groups_periods || [];

  useEffect(() => {
    if (!ownerUgpId) {
      setAudienceOptions({ groups_periods: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingAudience(true);
      try {
        const res = await fetch(
          `/api/ugp-properties/audience-options?user_groups_periods_id=${encodeURIComponent(ownerUgpId)}`
        );
        const data = await res.json();
        if (!cancelled && data.success) {
          setAudienceOptions({
            groups_periods: data.data?.groups_periods || []
          });
        } else if (!cancelled) {
          setAudienceOptions({ groups_periods: [] });
        }
      } catch {
        if (!cancelled) setAudienceOptions({ groups_periods: [] });
      } finally {
        if (!cancelled) setLoadingAudience(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerUgpId]);

  const selectedGpLabel = () => {
    const hit = orgGroupsPeriods.find((o) => String(o.id) === String(audienceGpId));
    return hit?.label || '';
  };

  const pickDefaultGpId = () => {
    if (!orgGroupsPeriods.length) return '';
    if (ownerGroupsPeriodsId) {
      const match = orgGroupsPeriods.find((o) => String(o.id) === String(ownerGroupsPeriodsId));
      if (match) return match.id;
    }
    return orgGroupsPeriods[0].id;
  };

  const openAdd = () => {
    setEditing(null);
    setPropertyKey(enabledKeys[0] || 'meeting_link');
    setAudienceKind('everyone');
    setAudienceGpId(pickDefaultGpId());
    setValue('');
    setError('');
    setSheetOpen(true);
  };

  useEffect(() => {
    if (addSheetOpen) openAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- header + triggers add sheet
  }, [addSheetOpen]);

  const openEdit = (row) => {
    setEditing(row);
    setPropertyKey(row.property_key);
    if (row.audience === 'groups_periods' && row.audience_id) {
      setAudienceKind('groups_periods');
      setAudienceGpId(row.audience_id);
    } else {
      setAudienceKind('everyone');
      setAudienceGpId(pickDefaultGpId());
    }
    setValue(row.value || '');
    setError('');
    setSheetOpen(true);
  };

  const closeSheet = () => {
    if (submitting) return;
    setSheetOpen(false);
    setEditing(null);
    if (onAddSheetOpenChange) onAddSheetOpenChange(false);
  };

  const handleAudienceChange = (e) => {
    const v = e.target.value;
    if (v === 'everyone') {
      setAudienceKind('everyone');
      return;
    }
    setAudienceKind('groups_periods');
    setAudienceGpId(v);
  };

  const audienceSelectValue = () => {
    if (audienceKind === 'everyone') return 'everyone';
    return audienceGpId || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!ownerUgpId) {
      setError('No active enrollment for this profile.');
      return;
    }
    if (audienceKind === 'groups_periods' && !audienceGpId) {
      setError('Select a group period for the audience.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        value: value.trim(),
        audience_kind: audienceKind,
        audience_id: audienceKind === 'groups_periods' ? audienceGpId : undefined,
        label: audienceKind === 'groups_periods' ? selectedGpLabel() : undefined
      };
      let res;
      if (editing?.id) {
        res = await fetch(`/api/ugp-properties/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        res = await fetch('/api/ugp-properties', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_groups_periods_id: ownerUgpId,
            property_key: propertyKey,
            ...body
          })
        });
      }
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to save');
        return;
      }
      setSheetOpen(false);
      setEditing(null);
      if (onAddSheetOpenChange) onAddSheetOpenChange(false);
      if (onReload) await onReload();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!canRemove || !window.confirm(`Remove ${getPropertyLabel(row.property_key, assignmentMetadata)}?`)) {
      return;
    }
    try {
      const res = await fetch(`/api/ugp-properties/${row.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success && onReload) await onReload();
    } catch {
      /* ignore */
    }
  };

  const audienceDisabled =
    submitting || (audienceKind === 'groups_periods' && !orgGroupsPeriods.length && !loadingAudience);

  return (
    <>
      {loading ? (
        <p className="profile-field-hint--subtle">Loading properties…</p>
      ) : properties.length === 0 ? (
        <p style={{ fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>
          No profile properties yet.
          {canAdd ? ' Use + Add New in the section header.' : null}
        </p>
      ) : (
        <div className="profile-ugp-props-list">
          {properties.map((row) => {
            const propertyLabel = getPropertyLabel(row.property_key, assignmentMetadata);
            const isUrlWithValue = row.property_kind === 'url' && row.value;
            return (
            <div key={row.id} className="profile-ugp-props-row">
              <div className="profile-ugp-props-row-main">
                <span className="profile-ugp-props-key">{propertyLabel}</span>
                <span className="profile-ugp-props-audience">{formatAudienceLabel(row)}</span>
                {row.property_kind !== 'url' && row.value ? (
                  <span className="profile-ugp-props-value">{row.value}</span>
                ) : null}
              </div>
              {(isUrlWithValue || canEdit || canRemove) && (
                <div className="profile-ugp-props-actions">
                  {isUrlWithValue && (
                    <a
                      href={row.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="profile-document-edit"
                      aria-label={`Open ${propertyLabel} in new tab`}
                      title="Open in new tab"
                    >
                      <UgpPropertyOpenTabIcon />
                    </a>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      className="profile-document-edit"
                      onClick={() => openEdit(row)}
                      aria-label={`Edit ${propertyLabel}`}
                      title="Edit property"
                    >
                      <UgpPropertyEditIcon />
                    </button>
                  )}
                  {canRemove && (
                    <button
                      type="button"
                      className="profile-document-delete"
                      onClick={() => handleDelete(row)}
                      aria-label={`Remove ${propertyLabel}`}
                      title="Remove property"
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      <ProfileFormSheet
        open={sheetOpen}
        onClose={closeSheet}
        closeDisabled={submitting}
        title={editing ? 'Edit property' : 'Add property'}
        titleId="ugp-property-sheet-title"
        subtext="Values are shared with linked profiles when audience matches (e.g. students see the counselor link for their group period)."
        footer={
          <button
            type="submit"
            form="ugp-property-form"
            className="profile-sheet-btn profile-sheet-btn--primary"
            disabled={
              submitting ||
              !value.trim() ||
              (!editing && !propertyKey) ||
              (audienceKind === 'groups_periods' && !audienceGpId)
            }
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        }
      >
        <form id="ugp-property-form" onSubmit={handleSubmit}>
          {!editing && (
            <div className="profile-sheet-form-group">
              <label className="profile-sheet-label" htmlFor="ugp-prop-key">
                Property
              </label>
              <select
                id="ugp-prop-key"
                className="profile-sheet-select"
                value={propertyKey}
                onChange={(e) => setPropertyKey(e.target.value)}
                disabled={submitting}
                required
              >
                {enabledKeys.map((k) => (
                  <option key={k} value={k}>
                    {defs[k]?.label || k}
                  </option>
                ))}
              </select>
            </div>
          )}
          {editing && (
            <div className="profile-sheet-form-group">
              <span className="profile-sheet-label">Property</span>
              <p className="profile-sheet-muted">
                {getPropertyLabel(editing.property_key, assignmentMetadata)}
              </p>
            </div>
          )}
          <div className="profile-sheet-form-group">
            <label className="profile-sheet-label" htmlFor="ugp-prop-audience">
              Audience
            </label>
            <select
              id="ugp-prop-audience"
              className="profile-sheet-select"
              value={audienceSelectValue()}
              onChange={handleAudienceChange}
              disabled={audienceDisabled}
            >
              <option value={AUDIENCE_EVERYONE.value}>{AUDIENCE_EVERYONE.label}</option>
              {orgGroupsPeriods.length > 0 ? (
                <optgroup label="Group periods in this organization">
                  {orgGroupsPeriods.map((gp) => (
                    <option key={gp.id} value={gp.id}>
                      {gp.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                !loadingAudience && (
                  <option value="" disabled>
                    No group periods in this organization
                  </option>
                )
              )}
            </select>
            <p className="profile-sheet-hint">
              {loadingAudience
                ? 'Loading group periods…'
                : 'Everyone = all viewers who may see this property. Group periods are limited to this profile’s organization.'}
            </p>
          </div>
          <div className="profile-sheet-form-group">
            <label className="profile-sheet-label" htmlFor="ugp-prop-value">
              Value
            </label>
            <input
              id="ugp-prop-value"
              type="url"
              className="profile-sheet-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="https://…"
              required
              disabled={submitting}
            />
          </div>
          {error ? <div className="profile-sheet-error">{error}</div> : null}
        </form>
      </ProfileFormSheet>
    </>
  );
};

export default UgpPropertiesSection;
